import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './dateInput.css';

/**
 * Sabit rapor kriterleri için TARİH kutusu.
 *
 * NEDEN: kriter formları `<input type="date">` kullanıyordu — takvimi tarayıcı çiziyordu.
 * Chrome'un yerel picker'ı uygulamanın tasarım diline hiç uymuyor (İngilizce ay/gün adları,
 * kendi tipografisi, temaya duyarsız, koyu temada beyaz kalıyor) ve tarayıcıdan tarayıcıya
 * değişiyordu. Bu bileşen takvimi kendisi çizer: MultiSelect ile aynı yükseklik/kenarlık/
 * yarıçap, Türkçe ay–gün adları, pazartesi başlangıçlı hafta, tokens.css üzerinden tema.
 *
 * Sözleşme — `<input type="date">` ile AYNI veri biçimi, böylece çağıran taraf değişmez:
 *   value    — 'YYYY-MM-DD' ya da '' (boş)
 *   onChange — (isoString) => void   ('' = temizlendi)
 * Ekranda tarih Türkçe okunuşuyla (gg.aa.yyyy) gösterilir.
 */

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const DAYS = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pa'];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** 'YYYY-MM-DD' -> {y,m,d} (m 0-tabanlı); geçersizse null. Date() ile parse ETME: saat
 *  dilimi kaydırması günü bir geri alabiliyor. */
function parseIso(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v ?? '').trim());
  if (!m) return null;
  const y = +m[1]; const mo = +m[2] - 1; const d = +m[3];
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

const fmtTr = (v) => { const p = parseIso(v); return p ? `${pad(p.d)}.${pad(p.m + 1)}.${p.y}` : ''; };

/** Ayın ızgarası: pazartesi başlangıçlı, önceki/sonraki ayın taşan günleriyle 6 satır. */
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;           // Pazar=0 -> pazartesi başlangıcına çevir
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = lead - 1; i >= 0; i -= 1) cells.push({ d: daysInPrev - i, out: true, m: month - 1 });
  for (let d = 1; d <= daysInMonth; d += 1) cells.push({ d, out: false, m: month });
  for (let d = 1; cells.length < 42; d += 1) cells.push({ d, out: true, m: month + 1 });
  return cells;
}

export function DateInput({ value = '', onChange, placeholder = 'gg.aa.yyyy', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('days');        // 'days' | 'months' | 'years'
  const boxRef = useRef(null);
  const popRef = useRef(null);
  const [drop, setDrop] = useState('down');

  const today = useMemo(() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() }; }, []);
  const selected = useMemo(() => parseIso(value), [value]);

  // Açıkken gezinilen ay. Kutu her açıldığında seçili tarihe (yoksa bugüne) döner.
  const [cursor, setCursor] = useState(() => (selected ? { y: selected.y, m: selected.m } : { y: today.y, m: today.m }));
  useEffect(() => {
    if (!open) return;
    setView('days');
    setCursor(selected ? { y: selected.y, m: selected.m } : { y: today.y, m: today.m });
  }, [open, selected, today.y, today.m]);

  // Dışarı tıklama + Esc ile kapan.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Alanın altında yer yoksa takvimi yukarı aç — kriter formunun son satırındaki tarihler
  // sayfanın dibine denk geliyor ve takvim ekranın dışında kalıyordu.
  useLayoutEffect(() => {
    if (!open || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const need = popRef.current?.offsetHeight || 330;
    setDrop(r.bottom + need > window.innerHeight && r.top > need ? 'up' : 'down');
  }, [open]);

  const commit = (y, m, d) => { onChange?.(toIso(y, m, d)); setOpen(false); };
  const shiftMonth = (delta) => setCursor((c) => {
    const m = c.m + delta;
    return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
  });

  const cells = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);
  const yearBlock = useMemo(() => { const start = cursor.y - (((cursor.y % 12) + 12) % 12) % 12; return Array.from({ length: 12 }, (_, i) => start + i); }, [cursor.y]);
  const text = fmtTr(value);

  return (
    <div className={`rdi${disabled ? ' is-disabled' : ''}`} ref={boxRef}>
      <button
        type="button"
        className={`rdi-control${text ? ' has-value' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className="rdi-text">{text || placeholder}</span>
        {text && !disabled && (
          <span
            className="rdi-clear"
            role="button"
            tabIndex={-1}
            title="Tarihi temizle"
            onClick={(e) => { e.stopPropagation(); onChange?.(''); }}
          >
            ×
          </span>
        )}
        <span className="rdi-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <rect x="3" y="5" width="18" height="16" rx="3" />
            <path d="M3 10h18M8 3v4M16 3v4" />
          </svg>
        </span>
      </button>

      {open && (
        <div className={`rdi-pop drop-${drop}`} ref={popRef}>
          <div className="rdi-head">
            <button type="button" className="rdi-nav" title="Önceki" onClick={() => (view === 'days' ? shiftMonth(-1) : setCursor((c) => ({ ...c, y: c.y - 12 })))}>‹</button>
            <button type="button" className="rdi-title" onClick={() => setView((v) => (v === 'days' ? 'months' : v === 'months' ? 'years' : 'days'))}>
              {view === 'days' ? `${MONTHS[cursor.m]} ${cursor.y}` : view === 'months' ? cursor.y : `${yearBlock[0]} – ${yearBlock[11]}`}
            </button>
            <button type="button" className="rdi-nav" title="Sonraki" onClick={() => (view === 'days' ? shiftMonth(1) : setCursor((c) => ({ ...c, y: c.y + 12 })))}>›</button>
          </div>

          {view === 'days' && (
            <>
              <div className="rdi-dow">{DAYS.map((d) => <span key={d}>{d}</span>)}</div>
              <div className="rdi-grid">
                {cells.map((c, i) => {
                  // Taşan hücrenin gerçek ay/yılı: m, -1 veya 12 olabilir.
                  const y = cursor.y + Math.floor(c.m / 12);
                  const m = ((c.m % 12) + 12) % 12;
                  const isSel = selected && selected.y === y && selected.m === m && selected.d === c.d;
                  const isToday = today.y === y && today.m === m && today.d === c.d;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`rdi-day${c.out ? ' out' : ''}${isSel ? ' sel' : ''}${isToday ? ' today' : ''}`}
                      onClick={() => commit(y, m, c.d)}
                    >
                      {c.d}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {view === 'months' && (
            <div className="rdi-grid cols3">
              {MONTHS.map((name, i) => (
                <button
                  key={name}
                  type="button"
                  className={`rdi-cell${selected && selected.y === cursor.y && selected.m === i ? ' sel' : ''}`}
                  onClick={() => { setCursor((c) => ({ ...c, m: i })); setView('days'); }}
                >
                  {name.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {view === 'years' && (
            <div className="rdi-grid cols3">
              {yearBlock.map((y) => (
                <button
                  key={y}
                  type="button"
                  className={`rdi-cell${selected && selected.y === y ? ' sel' : ''}`}
                  onClick={() => { setCursor((c) => ({ ...c, y })); setView('months'); }}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          <div className="rdi-foot">
            <button type="button" className="rdi-link" onClick={() => commit(today.y, today.m, today.d)}>Bugün</button>
            <button type="button" className="rdi-link muted" onClick={() => { onChange?.(''); setOpen(false); }}>Temizle</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateInput;
